import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { useLocation } from "react-router-dom";

import { canonicalShopDomain, currentClientShopDomain } from "../api/client";
import { getMeForShop } from "../api/me";
import { isStockyPublicBigIntId } from "../api/stocky_public_ids";
import type { MeResponse } from "../types";

export interface AuthoritativeShopState {
  data: MeResponse | undefined;
  requestedShopDomain: string | null;
  shopId: string | null;
  shopDomain: string | null;
  /** Stable identity used to partition shop-owned query and workflow state. */
  shopGeneration: string | null;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

/**
 * Resolve the current shop through `/api/me`, while partitioning that request
 * by the canonical shop visible to the embedded client. A cached or late
 * response for shop A can therefore never become authoritative for shop B.
 */
export function useAuthoritativeShop(): AuthoritativeShopState {
  const location = useLocation();
  const unresolvedQueryId = useId();
  const requestedShopDomain = currentClientShopDomain(location.search);
  const meQuery = useQuery({
    queryKey:
      requestedShopDomain === null
        ? ["blocked-authoritative-shop", unresolvedQueryId]
        : ["me", "shop-generation", requestedShopDomain],
    queryFn: () => {
      if (requestedShopDomain === null) {
        throw new Error("shop generation is unresolved");
      }
      return getMeForShop(requestedShopDomain);
    },
    enabled: requestedShopDomain !== null,
  });

  const responseShopDomain = canonicalShopDomain(
    meQuery.data?.shopify_domain,
  );
  const validIdentity =
    meQuery.data !== undefined &&
    typeof meQuery.data.shop_id === "string" &&
    isStockyPublicBigIntId(meQuery.data.shop_id) &&
    responseShopDomain !== null &&
    requestedShopDomain !== null &&
    responseShopDomain === requestedShopDomain;
  const data = validIdentity ? meQuery.data : undefined;
  const identityError = meQuery.isSuccess && !validIdentity;
  const shopId = data?.shop_id ?? null;
  const shopDomain = data ? responseShopDomain : null;

  return {
    data,
    requestedShopDomain,
    shopId,
    shopDomain,
    shopGeneration:
      shopId !== null && shopDomain !== null
        ? `${shopId}:${shopDomain}`
        : null,
    isLoading: requestedShopDomain === null || meQuery.isLoading,
    isError: meQuery.isError || identityError,
    retry: () => void meQuery.refetch(),
  };
}
